// Identity-claim and personalization fields — plan §8.1 (Actor, Person), §8.3 (Intent) and §11.2.
import { hasControlCharacter } from "./chars";
import type { Intent } from "./types";

export const DISPLAY_NAME_MAX_LENGTH = 50;

/** The starter avatars offered at claim time (public/img keys). */
export const STARTER_AVATAR_KEYS = [
  "a-red-cap",
  "a-headphones",
  "a-builder",
  "a-astronaut",
  "a-shades",
  "a-knight",
  "a-wizard",
  "a-robot",
] as const;

export type StarterAvatarKey = (typeof STARTER_AVATAR_KEYS)[number];

export const INTENTS: readonly Intent[] = [
  "find_assets",
  "share_work",
  "sell",
  "find_work",
  "hire",
  "collaborate",
  "join_communities",
  "run_business",
];

/** Private topic affinities offered as optional chips. */
export const TOPICS = [
  "scripting",
  "ui-ux",
  "3d-modeling",
  "building",
  "vfx",
  "animation",
  "audio",
  "game-design",
  "marketing",
  "project-management",
] as const;

export type Topic = (typeof TOPICS)[number];

/** Trims and collapses whitespace. Returns `null` when empty, too long or containing control characters. */
export function normalizeDisplayName(value: string): string | null {
  if (hasControlCharacter(value)) return null;
  const normalized = value.trim().replace(/\s+/g, " ");
  if (normalized.length === 0 || normalized.length > DISPLAY_NAME_MAX_LENGTH) return null;
  return normalized;
}

export function isStarterAvatarKey(value: unknown): value is StarterAvatarKey {
  return typeof value === "string" && (STARTER_AVATAR_KEYS as readonly string[]).includes(value);
}

function sanitizeList<T extends string>(values: unknown, allowed: readonly T[]): T[] | null {
  if (!Array.isArray(values)) return null;
  const result: T[] = [];
  for (const value of values) {
    if (typeof value !== "string" || !(allowed as readonly string[]).includes(value)) return null;
    if (!result.includes(value as T)) result.push(value as T);
  }
  return result;
}

/** Validates a client-supplied intent list: known values only, duplicates removed, original order kept. */
export function sanitizeIntents(values: unknown): Intent[] | null {
  return sanitizeList(values, INTENTS);
}

export function sanitizeTopics(values: unknown): Topic[] | null {
  return sanitizeList(values, TOPICS);
}
